<template>
  <intersect
    root-margin="150px 0px"
    @enter.once="getDeals"
  >
    <div
      v-show="showCollection"
      class="smart-collection-section"
      :style="{ minHeight: minHeight }"
    >
      <SpotlightCallout
        v-if="collection.component_type == 'spotlight_callout'"
        :id="(collection.id + '').replace('.', '-')"
        :deals="deals"
        :more="more"
        :component="component"
      />
      <SpotlightCarousel
        v-else-if="collection.component_type == 'spotlight'"
        :id="(collection.id + '').replace('.', '-')"
        :deals="deals"
        :more="more"
        :component="component"
      />
      <ProductCarousel
        v-if="collection.component_type == 'product_carousel'"
        :id="(collection.id + '').replace('.', '-')"
        :title="collection.title"
        :title-image="component.image"
        :deals="deals"
        :more="more"
        :relative-to-container="relativeToContainer"
      />
      <top-pick
        v-if="collection.component_type == 'top_picks'"
        :deals="deals"
        :more="more"
      />
      <countdown
        v-if="collection.component_type == 'countdown'"
        :more="more"
        :component="component"
        :start-date="startDate"
        :end-date="collection.end_date"
      />
      <Grid
        v-if="collection.component_type == 'grid'"
        :id="(collection.id + '').replace('.', '-')"
        :title="collection.title"
        :deals="deals"
      />

      <div v-if="showBlackFridayRoadblock">
        <BlackFridayRoadblock v-show="isLoggedIn && collection.component_type == 'explore'" />

        <EmailCaptureRoadblock v-if="!isLoggedIn && collection.component_type == 'email_capture'">
          <template #default>
            <h5>
              <span class="d-block">Here’s 10% off.</span>
              Invest in yourself.
            </h5>
            <p>
              <strong>Discover, buy, and sell</strong> tools to grow your business.
              Join the marketplace trusted by 1M+ entrepreneurs.
            </p>
          </template>

          <template #button>
            Unlock 10% off
          </template>
        </EmailCaptureRoadblock>
      </div>

      <!-- TODO: Check this if-else validation -->
      <div v-else>
        <!-- <div v-if="collection.component_type == 'email_capture'">
          <BlackFridayEmailRoadblock v-if="showBlackFridayEmailRoadblock" />
          <email-capture-roadblock />
        </div> -->
        <new-plus-promo-roadblock v-if="collection.component_type == 'plus_promo'" />
        <explore-roadblock v-if="collection.component_type == 'explore'" />
      </div>
    </div>
  </intersect>
</template>

<script>
import get from 'lodash/get';
import catchify from 'catchify';
import Intersect from 'vue-intersect';
import { DEALS_URL } from '@/constants/api';
import CampaignsMixin from '~/mixins/campaigns';
import ProductCarousel from '../common/ProductCarousel';
import Grid from './Grid.vue';
import SpotlightCarousel from '../common/SpotlightCarousel';
import SpotlightCallout from '../common/SpotlightCallout';
import TopPick from '../common/TopPick.vue';
import Countdown from '../common/Countdown.vue';
import ExploreRoadblock from '../roadblocks/ExploreRoadblock.vue';
import EmailCaptureRoadblock from '../roadblocks/EmailCaptureRoadblock.vue';
// import BlackFridayEmailRoadblock from '../roadblocks/BlackFridayEmailRoadblock.vue';
import BlackFridayRoadblock from '../roadblocks/BlackFridayRoadblock.vue';
import NewPlusPromoRoadblock from '../roadblocks/NewPlusPromoRoadblock.vue';

const substitutePlaceholder = (url) => {
  const parts = url.split('/');
  if (parts.pop() === 'as-taco.svg') {
    parts.push('as-taco-16:9.png');
    return parts.join('/');
  }

  return url;
};

export default {
  components: {
    Intersect,
    ProductCarousel,
    Grid,
    SpotlightCarousel,
    SpotlightCallout,
    TopPick,
    Countdown,
    ExploreRoadblock,
    EmailCaptureRoadblock,
    // BlackFridayEmailRoadblock,
    BlackFridayRoadblock,
    NewPlusPromoRoadblock,
  },
  mixins: [CampaignsMixin],
  props: {
    collection: {
      type: Object,
      required: true,
    },
    useIntersection: {
      type: Boolean,
      default: false,
    },
    // jw: determines whether the smart collection should be displayed relative to its container or
    //     relative to the width of the viewport (currently only used for product carousels).
    relativeToContainer: {
      type: Boolean,
      default: false,
    },
    minHeight: {
      type: String,
      default: '300px',
    },
  },
  data() {
    const preloadedDeals = Array.isArray(this.collection.deals) ? this.collection.deals : null;
    return {
      deals: preloadedDeals || [],
      isFetched: preloadedDeals !== null,
      showCollection: !(preloadedDeals !== null && preloadedDeals.length === 0),
    };
  },
  async fetch() {
    if (!this.useIntersection) {
      await this.getDeals();
    }
  },
  computed: {
    startDate() {
      return this.collection.start_date;
    },
    profile() {
      if (this.collection && this.collection.deal_profile) {
        return this.collection.deal_profile.slug;
      }

      return false;
    },
    more() {
      if (this.collection.root_level) {
        return `/${this.collection.slug}/`;
      }
      return `/collections/${this.collection.slug}/`;
    },
    component() {
      return {
        background: this.collection.component_background,
        background_color: this.collection.component_background_color,
        mobile_background: this.collection.component_mobile_background,
        image: this.collection.component_image,
        promo: this.collection.component_promo,
        cta_link: this.collection.component_cta_link,
        cta_text: this.collection.component_cta_text,
      };
    },
    isLoggedIn() {
      return !!get(this, '$store.state.session.user.email');
    },
  },
  methods: {
    async getDeals() {
      if (this.isFetched) {
        return;
      }

      this.isFetched = !this.isFetched;
      const url = this.generateApiUrl();

      if (!url) return;
      const [error, response] = await catchify(this.$cachios.get(url, { cdn: true }));

      if (!error) {
        const { data: { deals = [] } = {} } = response;
        this.deals = deals;
        for (let i = 0; i < this.deals.length; i += 1) {
          const deal = this.deals[i];
          this.deals[i].featured_image_url = substitutePlaceholder(deal.featured_image_url);
          this.deals[i].media_url = substitutePlaceholder(deal.media_url);
        }
      }

      // In case no Deals, hide blank section
      this.showCollection = (this.deals.length > 0);
    },
    generateApiUrl() {
      const col = this.collection;
      if (['countdown', 'email_capture', 'explore'].includes(col.component_type)) {
        return null;
      }
      const userId = (
        col.per_user_cache
        && this.$store.state.session
        && this.$store.state.session.user
        && this.$store.state.session.user.id
      ) || 'a';
      const urlParams = new URLSearchParams(col.query_parameters);
      const queryargs = [
        `per_page=${col.component_product_num || 12}`,
        `__cache=${userId}`,
      ];
      if (col.maximum_results) {
        queryargs.push(`maximum_results=${col.maximum_results}`);
      }
      urlParams.forEach((value, key) => {
        if ([
          'per_page',
          'status',
          'sort',
          'marketplace',
          'page',
          'query',
          'tags',
          'type',
        ].includes(key)) {
          queryargs.push(`${key}=${value}`);
        } else {
          queryargs.push(`profile{${key}}=${value}`);
        }
      });
      let url = `${DEALS_URL}/?${queryargs.join('&')}`;
      if (this.profile) {
        url += `&profile=${this.profile}`;
      }
      return url;
    },
  },
};
</script>
